import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import convert from 'color-convert';
import { isEmpty } from 'lodash';
import { ThemeContext } from 'styled-components';
import { Modal } from '@luigiclaudio/ga-baseline-ui/modal';
import { H6 } from '@luigiclaudio/ga-baseline-ui/typography';
import { Row, Col } from '@luigiclaudio/ga-baseline-ui/layout';
import ConversionItem from './ConversionItem';
import ConversionContainer from './ConversionContainer';
import ColorToolsContext from '../../context/ColorToolsContext';
import colorToolsUtils from '../../../utils/colorToolsUtils';
import DetailsItem from './DetailsItem';
import ConversionRowContainer from './ConversionRowContainer';
import useAllColorUserCache from '../../../services/useAllColorUserCache';
import handleOnColorClick from '../../../utils/handleOnColorClick';
import FeedbackContext from '../../feedbackContext/FeedbackContext';
import feedbackOnStageUtils from '../../../utils/feedbackOnStageUtils';
import UseToolsHorizontalScroll from './UseToolsHorizontalScroll';
import ColorModalCloseButton from '../../colorModal/ColorModalCloseButton';
import ColorModalCustomHeader from '../../colorModal/ColorModalCustomHeader';
import ColorModalTitle from '../../colorModal/ColorModalTitle';
import ColorNameLicense from '../../ColorNameLicense';

const ColorVariantActionsModal = ({
    colorValue,
    onRequestClose,
    onClickSetMainColorCallback,
    showSetAsMain,
    showAddRemoveFromLibrary,
    showRemoveFromSelection,
    setFieldValue,
    ...props
}) => {
    const theme = useContext(ThemeContext);
    const intl = useIntl();

    const { colorOnStage, setColorOnStage } = useContext(ColorToolsContext);
    const { setFeedbackOnStage } = useContext(FeedbackContext);

    const {
        allColorsUserFromClient,
        setAllColorsUserFromClient,
        createColor,
        deleteColor,
        user,
        pathname,
        client,
    } = useAllColorUserCache(
        () => setFeedbackOnStage(feedbackOnStageUtils.colorAddedToLibrary(colorValue, theme, intl)),
        () =>
            setFeedbackOnStage(
                feedbackOnStageUtils.colorRemovedFromLibrary(colorValue, theme, intl),
            ),
    );

    const isFavorite = allColorsUserFromClient?.allColors?.data?.find(
        (item) => item.color === colorValue,
    );

    const { colorLibraryLabel, colorLibraryIcon } = colorToolsUtils.colorInLibraryLabel(
        isFavorite,
        theme,
        intl,
        user,
    );

    const { setMainColorLabel, setMainColorIcon } = colorToolsUtils.setMainColorLabel(theme, intl);

    const { deselectColorLabel, deselectColorIcon } = colorToolsUtils.deselectColorLabel(
        theme,
        intl,
    );

    const commonActionListProperties = {
        btnType: 'link',
        skin: 'muted',
        size: 'md',
    };

    const footerActions = [];

    if (showRemoveFromSelection) {
        footerActions.push({
            label: deselectColorLabel,
            icon: deselectColorIcon,
            ...commonActionListProperties,
            onClick: () => {
                setFieldValue(`colorList.${colorValue}`, false);
                onRequestClose();
            },
        });
    }

    if (showAddRemoveFromLibrary) {
        footerActions.push({
            label: colorLibraryLabel,
            icon: colorLibraryIcon,
            ...commonActionListProperties,
            onClick: () => {
                handleOnColorClick({
                    currentColor: colorValue,
                    currentId: isFavorite?.id,
                    setAllColorsUserFromClient,
                    allColorsUserFromClient,
                    client,
                    createColor,
                    deleteColor,
                    user,
                    pathname,
                });
                if (!user) {
                    onRequestClose();
                }
            },
        });
    }

    if (showSetAsMain) {
        footerActions.push({
            label: setMainColorLabel,
            icon: setMainColorIcon,
            ...commonActionListProperties,
            disabled: colorValue?.slice(1) === colorOnStage,
            onClick: () => {
                onClickSetMainColorCallback();
                setColorOnStage(colorValue.slice(1));
                setFeedbackOnStage(feedbackOnStageUtils.setMainColor(colorValue, theme, intl));
            },
        });
    }

    return (
        <Modal
            title={
                <ColorNameLicense>
                    {colorToolsUtils.colorName(colorValue.slice(1))}
                </ColorNameLicense>
            }
            customHeader={
                <ColorModalCustomHeader
                    style={{ backgroundColor: colorValue }}
                    isFavorite={!!isFavorite}
                />
            }
            footerActions={!isEmpty(footerActions) && footerActions}
            showCloseButton={false}
            handleOnDismiss={onRequestClose}
            {...props}
        >
            <ColorModalCloseButton onRequestClose={onRequestClose} />
            <ColorModalTitle colorValue={colorValue} />
            <ConversionContainer>
                <ConversionRowContainer>
                    <Row justifyContent="flex-start">
                        <Col xxs={12} sm={12}>
                            <H6 color="muted">
                                <FormattedMessage id="colorDetails.hexadecimal.label" />
                            </H6>
                            <DetailsItem value={colorValue} copyValue={colorValue.slice(1)} />
                        </Col>
                        <ConversionItem
                            conversion={convert.hex.rgb}
                            conversionLabel="rgb"
                            colorValue={colorValue}
                        />
                    </Row>
                </ConversionRowContainer>
                <ConversionRowContainer>
                    <Row justifyContent="flex-start">
                        <ConversionItem
                            conversion={convert.hex.cmyk}
                            conversionLabel="cmyk"
                            colorValue={colorValue}
                        />
                        <ConversionItem
                            conversion={convert.hex.hsl}
                            conversionLabel="hsl"
                            colorValue={colorValue}
                        />
                    </Row>
                </ConversionRowContainer>
            </ConversionContainer>
            <UseToolsHorizontalScroll colorValue={colorValue} onClickCallback={onRequestClose} />
        </Modal>
    );
};

ColorVariantActionsModal.propTypes = {
    colorValue: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onClickSetMainColorCallback: PropTypes.func.isRequired,
    showSetAsMain: PropTypes.bool,
    showAddRemoveFromLibrary: PropTypes.bool,
    showRemoveFromSelection: PropTypes.bool,
    setFieldValue: PropTypes.func,
};

ColorVariantActionsModal.defaultProps = {
    showSetAsMain: false,
    showAddRemoveFromLibrary: false,
    showRemoveFromSelection: false,
    setFieldValue: () => {},
};

export default ColorVariantActionsModal;
